/**
 * Sprint 18: Unified Excel Intake
 * Sprint 24: Added SharePoint status chip (Mock/Live indicator)
 * Sprint 24+: Enhanced with Template v3 support, scope selection, and upload modal
 * 
 * Features:
 * - Template v3 with Instructions, Reference, Data, Resources sheets
 * - Scope chip: "Uploading into: Portfolio > Program > Project"
 * - Local multi-file + SharePoint/OneDrive link (mock mode now, Graph live when creds arrive)
 * - Dry-run preview mode
 * - Error CSV download
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download, 
  ChevronDown, ChevronUp, Clock, FileWarning, ExternalLink,
  Table, FileCheck, AlertTriangle, HelpCircle, Cloud, Settings,
  Layers, Plus, Files, History, RefreshCw, Info, FolderTree,
  Building2, Briefcase, FolderKanban, CloudOff, Link as LinkIcon
} from 'lucide-react';
import { api } from '../api/client';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import ExcelUploadModal from '../components/ExcelUploadModal';
import AutoSyncPanel from '../components/upload/AutoSyncPanel';
import { toast } from 'sonner';

const UnifiedExcelIntake = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showWarnings, setShowWarnings] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  // Sprint 24: SharePoint status
  const [sharepointStatus, setSharepointStatus] = useState(null);
  // Sprint 24+: Upload modal and scoped uploads
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('upload');
  const [projectHierarchy, setProjectHierarchy] = useState(null);
  const [expandedPortfolios, setExpandedPortfolios] = useState({});
  const [expandedPrograms, setExpandedPrograms] = useState({});
  
  // Get scope from URL params (for contextual uploads)
  const prefilledScope = {
    portfolio_id: searchParams.get('portfolio_id'),
    portfolio_name: searchParams.get('portfolio_name'),
    program_id: searchParams.get('program_id'),
    program_name: searchParams.get('program_name'),
    project_id: searchParams.get('project_id'),
    project_name: searchParams.get('project_name'),
  };

  useEffect(() => {
    loadUploadStatus();
    loadSharepointStatus();
    loadUploadHistory();
  }, []);

  const loadUploadStatus = async () => {
    try {
      const response = await api.getUploadStatus();
      setUploadStatus(response.data);
    } catch (error) {
      console.error('Failed to load upload status:', error);
    }
  };

  // Sprint 24: Load SharePoint integration status
  const loadSharepointStatus = async () => {
    try {
      const response = await api.get('/intake/excel/v3/status');
      setSharepointStatus(response.data);
    } catch (error) {
      console.error('Failed to load SharePoint status:', error);
    }
  };

  // Sprint 24+: Load upload history
  const loadUploadHistory = async () => {
    try {
      const response = await api.get('/intake/excel/v3/history?limit=10');
      setUploadHistory(response.data.uploads || []);
    } catch (error) {
      console.error('Failed to load upload history:', error);
    }
  };

  // Load project hierarchy with upload status
  const loadProjectHierarchy = async () => {
    try {
      const response = await api.get('/intake/excel/v3/project-uploads');
      setProjectHierarchy(response.data);
      // Auto-expand all portfolios
      const expanded = {};
      response.data.hierarchy?.forEach(p => { expanded[p.id] = true; });
      setExpandedPortfolios(expanded);
    } catch (error) {
      console.error('Failed to load project hierarchy:', error);
    }
  };

  const togglePortfolio = (portfolioId) => {
    setExpandedPortfolios(prev => ({ ...prev, [portfolioId]: !prev[portfolioId] }));
  };

  const toggleProgram = (programId) => {
    setExpandedPrograms(prev => ({ ...prev, [programId]: !prev[programId] }));
  };

  const handleUploadComplete = (data) => {
    setShowUploadModal(false);
    loadUploadHistory();
    loadUploadStatus();
  };

  const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // Check if template is legacy format
  const isLegacyTemplate = (result) => {
    if (!result) return false;
    // Legacy templates don't have financial estimate or governance columns
    const hasNewFields = result.counts?.demands > 0 || 
                         result.counts?.financial_estimates > 0 ||
                         result.counts?.governance_stages > 0;
    // If we have work items but no new fields, it's likely legacy
    return result.counts?.work_items > 0 && !hasNewFields;
  };

  const handleFileUpload = async (file) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setError('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.uploadUnifiedExcel(file);
      const data = response.data;
      
      if (data.success) {
        // Check for legacy template
        if (data.legacy_format_detected) {
          setError(
            'Legacy template format detected. Please download and use the new unified template. ' +
            'The new template includes: Demand fields, Financial Estimates, Governance Stages, and Stakeholders.'
          );
          setResult({ ...data, isLegacy: true });
        } else {
          setResult(data);
          loadUploadStatus();
        }
      } else {
        setError(data.error || data.message || 'Upload failed');
        setResult(data);
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData) {
        // Check for legacy format error
        if (errorData.legacy_format_detected || errorData.error?.includes('legacy')) {
          setError(
            'Legacy template format detected. Please download the new unified template from the link below.'
          );
          setResult({ ...errorData, isLegacy: true });
        } else {
          setError(errorData.error || errorData.message || errorData.detail || 'Failed to upload file');
          if (errorData.counts) {
            setResult(errorData);
          }
        }
      } else {
        setError(err.message || 'Failed to upload file. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('simpliview_token');
    
    try {
      // Try signed URL approach first (no auth header required in new tab)
      const signedUrlResponse = await api.post('/intake/excel/v3/template/signed-url');
      const { url } = signedUrlResponse.data;
      window.location.href = `${API_URL}${url}`;
    } catch (signedUrlError) {
      console.debug('Signed URL failed, trying blob download:', signedUrlError);
      
      try {
        // Fallback: Fetch the template with authentication via Blob
        const response = await fetch(`${API_URL}/api/intake/excel/v3/template.xlsx`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to download template');
        }
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ScopeOps_Upload_v3.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        // Last resort fallback
        const templateUrl = `${API_URL}/api/intake/excel/v3/template.xlsx`;
        window.open(templateUrl, '_blank');
      }
    }
  };

  // Handle Data Dictionary download via signed URL
  const handleDataDictionaryDownload = async (format) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Get signed URL
      const signedUrlResponse = await api.post(`/intake/excel/v3/data-dictionary/signed-url?format=${format}`);
      const { url } = signedUrlResponse.data;
      
      if (format === 'csv') {
        // For CSV, trigger download
        window.location.href = `${API_URL}${url}`;
      } else {
        // For JSON, open in new tab
        window.open(`${API_URL}${url}`, '_blank');
      }
    } catch (error) {
      console.error('Data Dictionary download failed:', error);
      toast.error('Failed to download Data Dictionary. Please try again.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[var(--clr-text-primary)] mb-2 flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-[var(--clr-brand-primary)]" />
              Excel Data Intake
            </h1>
            <p className="text-[var(--clr-text-secondary)]">
              Import portfolio data using the unified template. Includes work items, demands, financials, governance, and stakeholders.
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Upload Button */}
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-[var(--clr-brand-primary)] hover:bg-[var(--clr-brand-primary-hover)]"
              data-testid="open-upload-modal-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Files
            </Button>
          </div>
        </div>

        {/* Prefilled Scope Indicator */}
        {(prefilledScope.portfolio_id || prefilledScope.program_id || prefilledScope.project_id) && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2" data-testid="prefilled-scope-chip">
            <Layers className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Pre-selected scope:</span>
            <span className="text-sm text-blue-900">
              {[prefilledScope.portfolio_name, prefilledScope.program_name, prefilledScope.project_name]
                .filter(Boolean)
                .join(' › ') || 'Global'}
            </span>
          </div>
        )}

        {/* Last Upload Status */}
        {uploadStatus?.filename && (
          <div className="mt-4 inline-flex items-center gap-3 px-4 py-2.5 bg-[var(--clr-sev-healthy)] bg-opacity-10 border border-green-200 rounded-lg text-sm" data-testid="last-upload-status">
            <CheckCircle className="w-4 h-4 text-[var(--clr-sev-healthy)]" />
            <span className="text-[var(--clr-text-secondary)]">Last upload:</span>
            <span className="font-medium text-[var(--clr-text-primary)]">{uploadStatus.filename}</span>
            <Clock className="w-4 h-4 text-[var(--clr-text-muted)] ml-2" />
            <span className="text-[var(--clr-text-muted)]">{formatDate(uploadStatus.timestamp)}</span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="upload" className="flex items-center gap-2" data-testid="tab-upload">
            <Upload className="w-4 h-4" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="by-project" className="flex items-center gap-2" data-testid="tab-by-project" onClick={() => { if (!projectHierarchy) loadProjectHierarchy(); }}>
            <FolderTree className="w-4 h-4" />
            By Project
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="sharepoint" className="flex items-center gap-2" data-testid="tab-sharepoint">
            <Cloud className="w-4 h-4" />
            SharePoint Sync
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="mt-4">

      {/* Download Template - Prominent */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <Download className="w-5 h-5 text-[var(--clr-brand-primary)]" />
              Unified Template (v3)
            </h3>
            <p className="text-sm text-[var(--clr-text-secondary)] mt-1">
              Complete template with work items, risks, issues, decisions, dependencies, sprints, and releases
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-[var(--clr-text-muted)]">
              <span className="flex items-center gap-1">
                <Table className="w-3 h-3" />
                10 sheets: Data + 6 domain sheets
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                All dropdown validations included
              </span>
            </div>
          </div>
          <button
            onClick={handleDownloadTemplate}
            className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] transition-colors flex items-center gap-2"
            data-testid="download-unified-template"
          >
            <Download className="w-4 h-4" />
            Download Template (.xlsx)
          </button>
        </div>
      </div>

      {/* Template v3 Preview Panel */}
      <div className="mb-6 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="template-preview-panel">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Files className="w-5 h-5 text-[var(--clr-brand-primary)]" />
              <h3 className="font-semibold text-[var(--clr-text-primary)]">Template v3 Structure</h3>
              <Badge variant="outline" className="text-xs">10 Sheets</Badge>
            </div>
            <button
              onClick={handleDownloadTemplate}
              className="text-sm text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" />
              Download v3
            </button>
          </div>
        </div>
        
        {/* Core Sheets Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
          {/* Instructions Sheet */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-[var(--clr-text-primary)] text-sm">Instructions</p>
                <p className="text-[10px] text-[var(--clr-text-muted)]">Sheet 1</p>
              </div>
            </div>
            <p className="text-xs text-[var(--clr-text-secondary)]">
              Import modes, column guide, formatting rules.
            </p>
          </div>
          
          {/* Reference Sheet */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Layers className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-[var(--clr-text-primary)] text-sm">Reference</p>
                <p className="text-[10px] text-[var(--clr-text-muted)]">Sheet 2</p>
              </div>
            </div>
            <p className="text-xs text-[var(--clr-text-secondary)]">
              Dropdowns: Type, Status, Priority, Risk/Issue/Decision lists.
            </p>
          </div>
          
          {/* Data Sheet */}
          <div className="p-4 bg-green-50 border-l-2 border-green-500">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <Table className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-900 text-sm">Data</p>
                <p className="text-[10px] text-green-700">Sheet 3 (Unified)</p>
              </div>
            </div>
            <p className="text-xs text-green-800 mb-1">
              Unified import: all record types in one sheet.
            </p>
            <p className="text-[10px] text-green-700">
              <strong>Types:</strong> Epic, Risk, Issue, Decision, Sprint...
            </p>
          </div>
          
          {/* Resources Sheet */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Files className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-[var(--clr-text-primary)] text-sm">Resources</p>
                <p className="text-[10px] text-[var(--clr-text-muted)]">Sheet 4</p>
              </div>
            </div>
            <p className="text-xs text-[var(--clr-text-secondary)]">
              Team members, roles, capacity, rates.
            </p>
          </div>
        </div>

        {/* Domain Sheets Row */}
        <div className="p-3 bg-purple-50 border-b border-purple-100">
          <p className="text-xs font-medium text-purple-700 mb-2">Domain Sheets (Alternative to Data sheet)</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <div className="bg-white rounded px-2 py-1.5 border border-purple-200">
              <p className="text-xs font-medium text-purple-900">Risks</p>
              <p className="text-[10px] text-purple-600">Impact, Likelihood, Mitigation</p>
            </div>
            <div className="bg-white rounded px-2 py-1.5 border border-purple-200">
              <p className="text-xs font-medium text-purple-900">Issues</p>
              <p className="text-[10px] text-purple-600">Severity, Root Cause</p>
            </div>
            <div className="bg-white rounded px-2 py-1.5 border border-purple-200">
              <p className="text-xs font-medium text-purple-900">Decisions</p>
              <p className="text-[10px] text-purple-600">Status, Rationale</p>
            </div>
            <div className="bg-white rounded px-2 py-1.5 border border-purple-200">
              <p className="text-xs font-medium text-purple-900">Dependencies</p>
              <p className="text-[10px] text-purple-600">Kind, Source, Target</p>
            </div>
            <div className="bg-white rounded px-2 py-1.5 border border-purple-200">
              <p className="text-xs font-medium text-purple-900">Sprints</p>
              <p className="text-[10px] text-purple-600">Goal, Status</p>
            </div>
            <div className="bg-white rounded px-2 py-1.5 border border-purple-200">
              <p className="text-xs font-medium text-purple-900">Releases</p>
              <p className="text-[10px] text-purple-600">Date, Status</p>
            </div>
          </div>
        </div>
        
        {/* Data Dictionary Links */}
        <div className="p-3 bg-blue-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700">
              <strong>Data Dictionary:</strong> Complete mapping for all 112 columns
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleDataDictionaryDownload('json')}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 bg-transparent border-none cursor-pointer"
              data-testid="data-dictionary-json-link"
            >
              JSON
              <ExternalLink className="w-3 h-3" />
            </button>
            <button
              onClick={() => handleDataDictionaryDownload('csv')}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 bg-transparent border-none cursor-pointer"
              data-testid="data-dictionary-csv-link"
            >
              CSV
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
          dragActive
            ? 'border-blue-500 bg-[var(--clr-brand-primary)] bg-opacity-10'
            : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-[var(--clr-surface-hover)]'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="upload-dropzone"
      >
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          disabled={uploading}
          className="hidden"
          id="file-upload"
          data-testid="file-upload-input"
        />
        
        <label htmlFor="file-upload" className="cursor-pointer">
          <div className="flex flex-col items-center">
            {uploading ? (
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[var(--clr-brand-primary)] mb-4"></div>
            ) : (
              <Upload className="w-16 h-16 text-[var(--clr-brand-primary)] mb-4" />
            )}
            
            <p className="text-lg font-semibold text-[var(--clr-text-primary)] mb-2">
              {uploading ? 'Uploading and processing...' : 'Drop your Excel file here'}
            </p>
            <p className="text-sm text-[var(--clr-text-secondary)] mb-4">or click to browse</p>
            <p className="text-xs text-[var(--clr-text-muted)]">Supported formats: .xlsx, .xls (Max 20MB)</p>
          </div>
        </label>

        {!uploading && (
          <div className="mt-6">
            <label
              htmlFor="file-upload"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--clr-brand-primary)] text-white font-medium rounded-lg hover:bg-[var(--clr-brand-primary-hover)] transition-colors cursor-pointer"
              data-testid="choose-file-button"
            >
              <FileSpreadsheet className="w-5 h-5" />
              Choose File
            </label>
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="mt-8 space-y-6">
          <div className={`rounded-xl p-6 border ${
            result.success && !result.isLegacy 
              ? 'bg-[var(--clr-sev-healthy)] bg-opacity-10 border-green-200' 
              : 'bg-[var(--clr-sev-blocked)] bg-opacity-10 border-red-200'
          }`}>
            <div className="flex items-start gap-4">
              {result.success && !result.isLegacy ? (
                <CheckCircle className="w-6 h-6 text-[var(--clr-sev-healthy)] flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 text-[var(--clr-sev-blocked)] flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className={`text-lg font-semibold mb-2 ${
                  result.success && !result.isLegacy ? 'text-green-900' : 'text-red-900'
                }`}>
                  {result.success && !result.isLegacy ? 'Upload Successful!' : 
                   result.isLegacy ? 'Legacy Template Detected' : 'Upload Failed'}
                </h3>
                
                {result.isLegacy && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <FileWarning className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          Your template is missing required columns for the unified format.
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          Please download the new template and re-import your data.
                        </p>
                        <button
                          onClick={handleDownloadTemplate}
                          className="mt-3 text-sm text-[var(--clr-brand-primary)] font-medium hover:underline flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" />
                          Download New Template
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {result.message && <p className="text-sm text-gray-700 mb-4">{result.message}</p>}

                {result.counts && (
                  <div className="bg-[var(--clr-surface-card)] rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-[var(--clr-text-primary)] mb-3">Ingested Records</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(result.counts).map(([key, count]) => (
                        <div key={key} className="flex items-center justify-between bg-[var(--clr-surface-subtle)] rounded-lg px-3 py-2">
                          <span className="text-xs text-[var(--clr-text-secondary)] capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="text-lg font-semibold text-[var(--clr-text-primary)]">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.warnings && result.warnings.length > 0 && (
                  <div className="bg-[var(--clr-sev-atrisk)] bg-opacity-10 rounded-lg p-4 border border-yellow-200">
                    <button
                      onClick={() => setShowWarnings(!showWarnings)}
                      className="flex items-center justify-between w-full text-left"
                      data-testid="toggle-warnings"
                    >
                      <h4 className="text-sm font-semibold text-yellow-900 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Warnings ({result.warnings.length})
                      </h4>
                      {showWarnings ? <ChevronUp className="w-4 h-4 text-yellow-700" /> : <ChevronDown className="w-4 h-4 text-yellow-700" />}
                    </button>
                    {showWarnings && (
                      <ul className="mt-3 space-y-1 text-sm text-yellow-800">
                        {result.warnings.slice(0, 10).map((warning, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-[var(--clr-sev-atrisk)] mt-1">•</span>
                            <span>{warning}</span>
                          </li>
                        ))}
                        {result.warnings.length > 10 && (
                          <li className="text-[var(--clr-sev-atrisk)] italic">... and {result.warnings.length - 10} more</li>
                        )}
                      </ul>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-3 mt-6">
                  {result.success && !result.isLegacy && (
                    <button
                      onClick={() => navigate('/work-items')}
                      className="px-6 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                      data-testid="proceed-to-work-items"
                    >
                      View Work Items
                    </button>
                  )}
                  <button
                    onClick={() => { setResult(null); setError(null); }}
                    className="px-6 py-2.5 bg-[var(--clr-surface-subtle)] text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Upload Another File
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && !result && (
        <div className="mt-6 bg-[var(--clr-sev-blocked)] bg-opacity-10 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[var(--clr-sev-blocked)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-900 font-medium">{error}</p>
              {error.includes('legacy') && (
                <button
                  onClick={handleDownloadTemplate}
                  className="mt-2 text-sm text-[var(--clr-brand-primary)] font-medium hover:underline flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Download New Unified Template
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      {!result && !error && (
        <div className="mt-8">
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="flex items-center gap-2 text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)] mb-4"
          >
            <HelpCircle className="w-5 h-5" />
            <span className="font-medium">Template Requirements</span>
            {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showHelp && (
            <div className="bg-[var(--clr-brand-primary)] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Unified Template Sheets</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-2">
                    <Table className="w-4 h-4" /> WorkItems
                  </h4>
                  <ul className="text-sm text-blue-100 space-y-1">
                    <li>• Portfolio, Program, Project, Status</li>
                    <li>• Type (Epic/Feature/Story/Task)</li>
                    <li>• Start Date, Due Date</li>
                    <li>• Hours Est, Hours Act, Rate</li>
                  </ul>
                </div>

                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-2">
                    <FileCheck className="w-4 h-4" /> Demands (NEW)
                  </h4>
                  <ul className="text-sm text-blue-100 space-y-1">
                    <li>• Title, Description, Department</li>
                    <li>• Category, Impact, Urgency</li>
                    <li>• Strategic Alignment (0-5)</li>
                    <li>• Complexity, Business Value</li>
                  </ul>
                </div>

                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-2">
                    <Table className="w-4 h-4" /> FinancialEstimates (NEW)
                  </h4>
                  <ul className="text-sm text-blue-100 space-y-1">
                    <li>• Scope ID/Name reference</li>
                    <li>• Capex, Opex amounts</li>
                    <li>• Labor Cost, Non-Labor Cost</li>
                    <li>• Currency, Fiscal Year</li>
                  </ul>
                </div>

                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-2">
                    <FileCheck className="w-4 h-4" /> GovernanceStages (NEW)
                  </h4>
                  <ul className="text-sm text-blue-100 space-y-1">
                    <li>• Scope ID reference</li>
                    <li>• Stage, Decision Flag</li>
                    <li>• Decision Date, Approver</li>
                    <li>• Comments</li>
                  </ul>
                </div>

                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-2">
                    <Table className="w-4 h-4" /> Stakeholders (NEW)
                  </h4>
                  <ul className="text-sm text-blue-100 space-y-1">
                    <li>• Name, Email, Role</li>
                    <li>• Entity Type, Entity ID</li>
                    <li>• RACI, Influence, Interest</li>
                    <li>• Department, Notes</li>
                  </ul>
                </div>

                <div className="bg-white bg-opacity-10 rounded-lg p-4">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-2">
                    <Table className="w-4 h-4" /> Legacy Sheets
                  </h4>
                  <ul className="text-sm text-blue-100 space-y-1">
                    <li>• Issues & Risks</li>
                    <li>• Decisions</li>
                    <li>• Dependencies</li>
                    <li>• Sprints</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-[var(--clr-text-primary)]">Upload History</h3>
              <Button variant="outline" size="sm" onClick={loadUploadHistory}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
            
            {uploadHistory.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {uploadHistory.map((upload, idx) => (
                  <div key={idx} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => {
                    // Navigate to work items filtered by the upload if we have scope
                    if (upload.details?.scope?.project_id) {
                      navigate(`/work-items?project_id=${upload.details.scope.project_id}`);
                    }
                  }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className={`w-5 h-5 ${
                          upload.details?.rows_err > 0 ? 'text-red-500' : 'text-green-500'
                        }`} />
                        <div>
                          <p className="font-medium text-[var(--clr-text-primary)]">
                            {upload.details?.filename || upload.action?.replace(/_/g, ' ').replace('excel ', 'Excel Upload')}
                          </p>
                          <p className="text-xs text-[var(--clr-text-muted)]">
                            {formatDate(upload.timestamp)} • by {upload.user_email || upload.performed_by || 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-green-600 font-medium">{upload.details?.rows_ok || 0} records imported</p>
                        {upload.details?.rows_err > 0 && (
                          <p className="text-red-600">{upload.details.rows_err} errors</p>
                        )}
                      </div>
                    </div>
                    {upload.details?.scope && (upload.details.scope.portfolio_id || upload.details.scope.program_id || upload.details.scope.project_id) && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-[var(--clr-text-muted)]">
                        <Layers className="w-3 h-3" />
                        Scope: {[upload.details.scope.portfolio_name, upload.details.scope.program_name, upload.details.scope.project_name].filter(Boolean).join(' › ') || 'Global'}
                        <ExternalLink className="w-3 h-3 ml-2" />
                        <span className="text-blue-600 hover:underline">View data</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-[var(--clr-text-muted)]">
                <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No upload history yet</p>
                <p className="text-xs mt-1">Upload an Excel file to see your history here</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* By Project Tab - Hierarchy View */}
        <TabsContent value="by-project" className="mt-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
                  <FolderTree className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                  Upload Status by Project
                </h3>
                <p className="text-sm text-[var(--clr-text-secondary)] mt-1">
                  View which projects have uploaded data and their SharePoint sync status
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadProjectHierarchy}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {/* Summary Stats */}
            {projectHierarchy?.summary && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-[var(--clr-text-primary)]">{projectHierarchy.summary.total_portfolios}</div>
                  <div className="text-xs text-[var(--clr-text-muted)]">Portfolios</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-[var(--clr-text-primary)]">{projectHierarchy.summary.total_programs}</div>
                  <div className="text-xs text-[var(--clr-text-muted)]">Programs</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-[var(--clr-text-primary)]">{projectHierarchy.summary.total_projects}</div>
                  <div className="text-xs text-[var(--clr-text-muted)]">Projects</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{projectHierarchy.summary.projects_with_uploads}</div>
                  <div className="text-xs text-[var(--clr-text-muted)]">With Data</div>
                </div>
              </div>
            )}

            {/* Hierarchy Tree */}
            {projectHierarchy?.hierarchy?.length > 0 ? (
              <div className="space-y-3">
                {projectHierarchy.hierarchy.map((portfolio) => (
                  <div key={portfolio.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Portfolio Header */}
                    <div 
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-indigo-50 to-purple-50 cursor-pointer hover:bg-indigo-100 transition-colors"
                      onClick={() => togglePortfolio(portfolio.id)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedPortfolios[portfolio.id] ? <ChevronDown className="w-4 h-4 text-indigo-600" /> : <ChevronUp className="w-4 h-4 text-indigo-600" />}
                        <Building2 className="w-5 h-5 text-indigo-600" />
                        <span className="font-semibold text-[var(--clr-text-primary)]">{portfolio.name}</span>
                        <Badge variant="outline" className="text-xs">{portfolio.program_count} programs</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[var(--clr-text-muted)]">
                          {portfolio.projects_with_uploads}/{portfolio.total_projects} projects with data
                        </span>
                        {portfolio.projects_with_uploads === portfolio.total_projects && portfolio.total_projects > 0 ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                        )}
                      </div>
                    </div>

                    {/* Programs */}
                    {expandedPortfolios[portfolio.id] && portfolio.programs.map((program) => (
                      <div key={program.id} className="border-t border-gray-100">
                        {/* Program Header */}
                        <div 
                          className="flex items-center justify-between p-3 pl-8 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => toggleProgram(program.id)}
                        >
                          <div className="flex items-center gap-3">
                            {expandedPrograms[program.id] ? <ChevronDown className="w-4 h-4 text-slate-600" /> : <ChevronUp className="w-4 h-4 text-slate-600" />}
                            <Briefcase className="w-4 h-4 text-slate-600" />
                            <span className="font-medium text-[var(--clr-text-primary)]">{program.name}</span>
                            <Badge variant="outline" className="text-xs">{program.project_count} projects</Badge>
                          </div>
                          <span className="text-xs text-[var(--clr-text-muted)]">
                            {program.projects_with_uploads}/{program.project_count} with data
                          </span>
                        </div>

                        {/* Projects */}
                        {expandedPrograms[program.id] && program.projects.map((project) => (
                          <div key={project.id} className="flex items-center justify-between p-3 pl-16 border-t border-gray-50 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <FolderKanban className="w-4 h-4 text-gray-500" />
                              <span className="text-[var(--clr-text-primary)]">{project.name}</span>
                              {project.has_data ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">{project.upload_count} uploads</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">No data</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              {project.sharepoint_sync ? (
                                <div className="flex items-center gap-1 text-xs text-blue-600">
                                  <LinkIcon className="w-3 h-3" />
                                  SharePoint linked
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-xs text-gray-400">
                                  <CloudOff className="w-3 h-3" />
                                  No sync
                                </div>
                              )}
                              {project.last_upload && (
                                <span className="text-xs text-[var(--clr-text-muted)]">
                                  Last: {formatDate(project.last_upload)}
                                </span>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/delivery/excel-intake?project_id=${project.id}&project_name=${encodeURIComponent(project.name)}`);
                                  setShowUploadModal(true);
                                }}
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                Upload
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : projectHierarchy === null ? (
              <div className="p-8 text-center text-[var(--clr-text-muted)]">
                <RefreshCw className="w-10 h-10 mx-auto mb-2 opacity-50 animate-spin" />
                <p>Loading project hierarchy...</p>
              </div>
            ) : (
              <div className="p-8 text-center text-[var(--clr-text-muted)]">
                <FolderTree className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No portfolios or projects found</p>
                <p className="text-xs mt-1">Create portfolios and projects first, then upload data here</p>
              </div>
            )}

            {/* Orphan Projects (without portfolio/program) */}
            {projectHierarchy?.orphan_projects?.length > 0 && (
              <div className="mt-6 border border-amber-200 rounded-lg overflow-hidden">
                <div className="p-3 bg-amber-50 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-800">Projects without Portfolio/Program assignment</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {projectHierarchy.orphan_projects.map((project) => (
                    <div key={project.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <FolderKanban className="w-4 h-4 text-gray-500" />
                        <span>{project.name}</span>
                        {project.has_data ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">{project.upload_count} uploads</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-amber-600">No data</Badge>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          navigate(`/delivery/excel-intake?project_id=${project.id}&project_name=${encodeURIComponent(project.name)}`);
                          setShowUploadModal(true);
                        }}
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        Upload
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* SharePoint Sync Tab */}
        <TabsContent value="sharepoint" className="mt-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-blue-600" />
                  SharePoint Auto-Sync
                </h3>
                <p className="text-sm text-[var(--clr-text-secondary)] mt-1">
                  Link your project Excel sheets to SharePoint for automatic synchronization
                </p>
              </div>
            </div>
            
            <AutoSyncPanel projectId={prefilledScope.project_id} />
            
            {/* Instructions */}
            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Store your Excel template in SharePoint or OneDrive</li>
                <li>Configure the sync link above with the file URL</li>
                <li>Set sync frequency (hourly, daily, or on-demand)</li>
                <li>Changes in SharePoint automatically update ScopeOps</li>
              </ol>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Upload Modal */}
      <ExcelUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        scope={prefilledScope}
        onUploadComplete={handleUploadComplete}
        title="Upload Excel Data"
      />
    </div>
  );
};

export default UnifiedExcelIntake;
